import type { TrackingEventName } from '~/constants/trackingEvents'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'
import type {
  EstablishmentSearchBBox,
  EstablishmentSearchDisplayKind,
} from '~/types/EstablishmentSearch'
import type {
  EstablishmentProgramRow,
  EstablishmentSchoolRow,
} from '~/types/EstablishmentSearchRows'
import type {
  CookieConsentChoice,
  DetailTabKey,
  EstablishmentResultConsultedEntity,
  EstablishmentSearchFiltersPayload,
  EstablishmentSearchGeoPayload,
  EstablishmentSearchGeoTrigger,
  EstablishmentSearchResultPreviewItem,
  EstablishmentSearchTrackingContext,
  EstablishmentSearchTrigger,
  ProgramInteractionPayload,
  SignupMethod,
  SocialSignupMethod,
  TrackingEventPayloads,
  TrackingPersonProperties,
  TrackingUserProperties,
} from '~/types/Tracking'
import type { UserProfileDto } from '~/types/AccountProfile'
import type { MsalIdTokenClaims } from '~/types/msal'
import type { ProgramRecommendationObj } from '~/types/school'
import { bboxToEstablishmentSearchGeoLabels } from '~/utils/geocoding'
import { isFranceDefaultEstablishmentSearchBBox } from '~/utils/establishmentSearchUrl'
import { getMsalActiveAccountEmail } from '~/utils/msalUtils'

const ESTABLISHMENT_SEARCH_RESULT_PREVIEW_LIMIT: number = 15

/**
 * Mappe une reco programme vers le payload d'event PostHog.
 * @param {ProgramRecommendationObj} program - Programme concerné.
 * @param {DetailTabKey} source - Onglet panneau détail (ecoles, favoris…).
 * @returns {ProgramInteractionPayload} Propriétés normalisées.
 */
export function toProgramInteractionPayload(
  program: ProgramRecommendationObj,
  source: DetailTabKey,
): ProgramInteractionPayload {
  return {
    program_id: program.programId,
    program_name: program.programName,
    school_name: program.schoolName,
    city: program.city,
    source,
  }
}

/**
 * Transforme les lignes API en aperçu top N pour `establishment_search_executed.results.items`.
 * @param {EstablishmentSearchDisplayKind} displayKind - Mode école ou formation.
 * @param {EstablishmentSchoolRow[]} schoolRows - Résultats écoles.
 * @param {EstablishmentProgramRow[]} programRows - Résultats formations.
 * @returns {EstablishmentSearchResultPreviewItem[]} Ids, noms et villes visibles dans PostHog.
 */
export function toEstablishmentSearchResultPreviewItems(
  displayKind: EstablishmentSearchDisplayKind,
  schoolRows: EstablishmentSchoolRow[],
  programRows: EstablishmentProgramRow[],
): EstablishmentSearchResultPreviewItem[] {
  const rows: EstablishmentSchoolRow[] | EstablishmentProgramRow[] =
    displayKind === 'school' ? schoolRows : programRows

  return rows
    .slice(0, ESTABLISHMENT_SEARCH_RESULT_PREVIEW_LIMIT)
    .map(
      (
        row: EstablishmentSchoolRow | EstablishmentProgramRow,
      ): EstablishmentSearchResultPreviewItem => {
        if (displayKind === 'school') {
          const schoolRow: EstablishmentSchoolRow = row as EstablishmentSchoolRow
          return {
            id: schoolRow.card.id,
            name: schoolRow.card.Name,
            kind: 'school',
            city: schoolRow.card.Locations[0],
          }
        }

        const programRow: EstablishmentProgramRow = row as EstablishmentProgramRow
        return {
          id: programRow.program.id,
          name: programRow.program.Name,
          kind: 'program',
          school_name: programRow.program.SchoolName,
          city: programRow.program.LocationsString,
        }
      },
    )
}

/**
 * @param {EstablishmentSearchGeoPayload} geo - Zone géographique de la recherche.
 * @returns {Pick<EstablishmentSearchTrackingContext, 'geo_primary_city' | 'geo_primary_region' | 'geo_primary_country'>} Champs géo primaires.
 */
function pickEstablishmentSearchGeoPrimaryFields(
  geo: EstablishmentSearchGeoPayload,
): Pick<
  EstablishmentSearchTrackingContext,
  'geo_primary_city' | 'geo_primary_region' | 'geo_primary_country'
> {
  return {
    ...(geo.primary_city ? { geo_primary_city: geo.primary_city } : {}),
    ...(geo.primary_region ? { geo_primary_region: geo.primary_region } : {}),
    ...(geo.primary_country ? { geo_primary_country: geo.primary_country } : {}),
  }
}

/**
 * Détermine l'origine de la zone géographique pour PostHog.
 * @param {object} params - État carte et intent utilisateur.
 * @param {EstablishmentSearchGeoTrigger} params.pendingGeoTrigger - Intent géo posé avant le refetch.
 * @param {boolean} params.mapSearchOn - Case « rechercher sur la carte » cochée.
 * @param {EstablishmentSearchBBox} params.bbox - Bbox carte active.
 * @returns {EstablishmentSearchGeoTrigger} Origine de la bbox active.
 */
export function resolveEstablishmentSearchGeoTrigger(params: {
  pendingGeoTrigger: EstablishmentSearchGeoTrigger
  mapSearchOn: boolean
  bbox: EstablishmentSearchBBox
}): EstablishmentSearchGeoTrigger {
  if (
    !params.mapSearchOn &&
    isFranceDefaultEstablishmentSearchBBox(params.bbox) &&
    params.pendingGeoTrigger !== 'url_restored'
  ) {
    return 'default_france'
  }
  return params.pendingGeoTrigger
}

/**
 * Reverse geocode la bbox active → villes / régions / pays lisibles PostHog.
 * @param {EstablishmentSearchBBox} bbox - Zone carte.
 * @param {boolean} mapSearchOn - Case « rechercher sur la carte » cochée.
 * @param {EstablishmentSearchGeoTrigger} triggerGeo - Origine de la zone.
 * @returns {Promise<EstablishmentSearchGeoPayload>} Payload géo pour les events recherche.
 */
export async function buildEstablishmentSearchGeoPayload(
  bbox: EstablishmentSearchBBox,
  mapSearchOn: boolean,
  triggerGeo: EstablishmentSearchGeoTrigger,
): Promise<EstablishmentSearchGeoPayload> {
  const geoLabels = await bboxToEstablishmentSearchGeoLabels(bbox)
  return {
    map_search_enabled: mapSearchOn,
    trigger_geo: triggerGeo,
    countries: [...geoLabels.countries],
    regions: [...geoLabels.regions],
    cities: [...geoLabels.cities],
    ...(geoLabels.primary_city ? { primary_city: geoLabels.primary_city } : {}),
    ...(geoLabels.primary_region ? { primary_region: geoLabels.primary_region } : {}),
    ...(geoLabels.primary_country ? { primary_country: geoLabels.primary_country } : {}),
  }
}

/**
 * Snapshot de recherche mémorisé après chaque `establishment_search_executed`.
 * @param {object} params - Filtres, résultats et géo de la recherche aboutie.
 * @param {EstablishmentSearchDisplayKind} params.display_kind - Mode école ou formation.
 * @param {string} params.query - Texte de recherche appliqué.
 * @param {EstablishmentSearchFiltersPayload} params.filters - Libellés des filtres actifs.
 * @param {number} params.results_count - Nombre de résultats renvoyés par l'API.
 * @param {boolean} params.map_search_enabled - Recherche géographique via la carte activée.
 * @param {EstablishmentSearchGeoPayload} params.geo - Zone géographique lisible (ville, région, pays).
 * @returns {EstablishmentSearchTrackingContext} Contexte pour enrichir les clics « Consulter ».
 */
export function toEstablishmentSearchTrackingContext(params: {
  display_kind: EstablishmentSearchDisplayKind
  query: string
  filters: EstablishmentSearchFiltersPayload
  results_count: number
  map_search_enabled: boolean
  geo: EstablishmentSearchGeoPayload
}): EstablishmentSearchTrackingContext {
  return {
    display_kind: params.display_kind,
    query: params.query,
    filters: params.filters,
    results_count: params.results_count,
    map_search_enabled: params.map_search_enabled,
    ...pickEstablishmentSearchGeoPrimaryFields(params.geo),
  }
}

/**
 * Payload complet de `establishment_search_executed`.
 * @param {object} params - État recherche au moment du flush PostHog.
 * @param {EstablishmentSearchDisplayKind} params.display_kind - Mode école ou formation.
 * @param {string} params.query - Texte de recherche appliqué.
 * @param {EstablishmentSearchFiltersPayload} params.filters - Libellés des filtres actifs.
 * @param {EstablishmentSearchGeoPayload} params.geo - Zone géographique lisible (ville, région, pays).
 * @param {number} params.resultsCount - Nombre de résultats renvoyés par l'API.
 * @param {EstablishmentSearchResultPreviewItem[]} params.items - Aperçu top N des résultats affichés.
 * @param {EstablishmentSearchTrigger} params.trigger - Action utilisateur ayant déclenché le refetch.
 * @param {number} params.filterCount - Nombre total de valeurs de filtre sélectionnées.
 * @param {boolean} params.hasTextQuery - Recherche textuelle non vide.
 * @returns {TrackingEventPayloads[typeof TRACKING_EVENTS.ESTABLISHMENT_SEARCH_EXECUTED]} Propriétés event.
 */
export function toEstablishmentSearchExecutedPayload(params: {
  display_kind: EstablishmentSearchDisplayKind
  query: string
  filters: EstablishmentSearchFiltersPayload
  geo: EstablishmentSearchGeoPayload
  resultsCount: number
  items: EstablishmentSearchResultPreviewItem[]
  trigger: EstablishmentSearchTrigger
  filterCount: number
  hasTextQuery: boolean
}): TrackingEventPayloads[typeof TRACKING_EVENTS.ESTABLISHMENT_SEARCH_EXECUTED] {
  return {
    display_kind: params.display_kind,
    query: params.query,
    filters: params.filters,
    geo: params.geo,
    results: {
      count: params.resultsCount,
      items: params.items,
    },
    trigger: params.trigger,
    has_active_filters: params.filterCount > 0 || params.hasTextQuery,
    filter_count: params.filterCount,
  }
}

/**
 * Payload de `establishment_result_consulted` (clic « Consulter »).
 * @param {EstablishmentSearchTrackingContext} context - Dernière recherche aboutie (ou fallback).
 * @param {EstablishmentResultConsultedEntity} entity - École ou formation cliquée.
 * @param {number} listPosition - Index dans la liste (0-based).
 * @returns {TrackingEventPayloads[typeof TRACKING_EVENTS.ESTABLISHMENT_RESULT_CONSULTED]} Propriétés event.
 */
export function toEstablishmentResultConsultedPayload(
  context: EstablishmentSearchTrackingContext,
  entity: EstablishmentResultConsultedEntity,
  listPosition: number,
): TrackingEventPayloads[typeof TRACKING_EVENTS.ESTABLISHMENT_RESULT_CONSULTED] {
  return {
    display_kind: entity.kind,
    entity_id: entity.id,
    entity_name: entity.name,
    ...(entity.school_name ? { school_name: entity.school_name } : {}),
    ...(entity.city ? { city: entity.city } : {}),
    list_position: listPosition,
    open_source: 'card_link',
    search_query: context.query,
    search_filters: context.filters,
    results_count_at_click: context.results_count,
    map_search_enabled: context.map_search_enabled,
    ...(context.geo_primary_city ? { geo_primary_city: context.geo_primary_city } : {}),
    ...(context.geo_primary_region ? { geo_primary_region: context.geo_primary_region } : {}),
    ...(context.geo_primary_country ? { geo_primary_country: context.geo_primary_country } : {}),
  }
}

/**
 * Payload de `establishment_map_marker_clicked` (clic sur un marqueur de la carte).
 * @param {EstablishmentSearchTrackingContext} context - Dernière recherche aboutie (ou fallback).
 * @param {EstablishmentResultConsultedEntity} entity - École ou formation du marqueur cliqué.
 * @returns {TrackingEventPayloads[typeof TRACKING_EVENTS.ESTABLISHMENT_MAP_MARKER_CLICKED]} Propriétés event.
 */
export function toEstablishmentMapMarkerClickedPayload(
  context: EstablishmentSearchTrackingContext,
  entity: EstablishmentResultConsultedEntity,
): TrackingEventPayloads[typeof TRACKING_EVENTS.ESTABLISHMENT_MAP_MARKER_CLICKED] {
  return {
    display_kind: entity.kind,
    entity_id: entity.id,
    entity_name: entity.name,
    ...(entity.school_name ? { school_name: entity.school_name } : {}),
    search_query: context.query,
    search_filters: context.filters,
    results_count_at_click: context.results_count,
    map_search_enabled: context.map_search_enabled,
    ...(context.geo_primary_city ? { geo_primary_city: context.geo_primary_city } : {}),
    ...(context.geo_primary_region ? { geo_primary_region: context.geo_primary_region } : {}),
    ...(context.geo_primary_country ? { geo_primary_country: context.geo_primary_country } : {}),
  }
}

/**
 * Mappe une ligne école vers l'entité trackée au clic « Consulter ».
 * @param {EstablishmentSchoolRow} row - Ligne résultat.
 * @returns {EstablishmentResultConsultedEntity} Entité normalisée.
 */
export function toEstablishmentResultConsultedEntityFromSchoolRow(
  row: EstablishmentSchoolRow,
): EstablishmentResultConsultedEntity {
  return {
    id: row.card.id,
    name: row.card.Name,
    kind: 'school',
    city: row.card.Locations[0],
  }
}

/**
 * Mappe une ligne formation vers l'entité trackée au clic « Consulter ».
 * @param {EstablishmentProgramRow} row - Ligne résultat.
 * @returns {EstablishmentResultConsultedEntity} Entité normalisée.
 */
export function toEstablishmentResultConsultedEntityFromProgramRow(
  row: EstablishmentProgramRow,
): EstablishmentResultConsultedEntity {
  return {
    id: row.program.id,
    name: row.program.Name,
    kind: 'program',
    school_name: row.program.SchoolName,
    city: row.program.LocationsString,
  }
}

/**
 * API publique du composable de tracking.
 */
export interface UseTrackingReturn {
  /** Émet un event custom typé vers PostHog (no-op côté serveur ou sans consentement). */
  track: <E extends TrackingEventName>(event: E, properties?: TrackingEventPayloads[E]) => void
  /** Rattache l'identité utilisateur (recolle l'historique anonyme + UTM). */
  identify: (distinctId: string, properties?: TrackingUserProperties) => void
  /** Enrichit le profil de la personne (segmentation libre dans Max AI / le MCP). */
  setPersonProperties: (properties: TrackingPersonProperties) => void
  /** Réinitialise l'identité (à appeler au logout). */
  reset: () => void
}

/**
 * Mappe les propriétés métier vers les champs PostHog (`$email`, `$name`, etc.).
 * @param {TrackingUserProperties | undefined} props - Propriétés utilisateur.
 * @returns {Record<string, string>} Payload passé à `posthog.identify`.
 */
function toPostHogIdentifyProperties(props?: TrackingUserProperties): Record<string, string> {
  if (!props) return {}

  const out: Record<string, string> = {}
  if (props.email) out.$email = props.email
  if (props.name) out.$name = props.name
  if (props.firstName) out.first_name = props.firstName
  if (props.lastName) out.last_name = props.lastName
  if (props.phone) out.phone = props.phone
  if (props.school_level) out.school_level = props.school_level
  if (props.source) out.acquisition_source = props.source
  return out
}

const SIGNUP_TRACKED_STORAGE_PREFIX = 'prepeers:signup-tracked:' as const

/**
 * @param {string} userId - Identifiant utilisateur stable.
 * @returns {boolean} `true` si `signup_completed` a déjà été posé pour cet utilisateur.
 */
function hasTrackedSignupForUser(userId: string): boolean {
  if (!import.meta.client || userId.trim() === '') return false
  return localStorage.getItem(`${SIGNUP_TRACKED_STORAGE_PREFIX}${userId}`) === '1'
}

/**
 * Marque `signup_completed` comme déjà émis pour éviter les doublons OAuth.
 * @param {string} userId - Identifiant utilisateur stable.
 * @returns {void}
 */
export function markSignupTrackedForUser(userId: string): void {
  if (!import.meta.client || userId.trim() === '') return
  localStorage.setItem(`${SIGNUP_TRACKED_STORAGE_PREFIX}${userId}`, '1')
}

/**
 * Indique si le jeton CIAM correspond à une création de compte.
 * @param {MsalIdTokenClaims} claims - Claims parsés du jeton d'identité.
 * @returns {boolean} `true` si inscription détectée.
 */
function isMsalNewUserClaim(claims: MsalIdTokenClaims): boolean {
  return claims.newUser === true || claims.isNewUser === true
}

/**
 * Déduit la méthode d'inscription depuis le claim `idp` MSAL.
 * @param {string | undefined} idp - Fournisseur d'identité (ex. `google.com`).
 * @returns {SignupMethod | null} Méthode reconnue ou `null`.
 */
function idpToSignupMethod(idp: string | undefined): SignupMethod | null {
  if (!idp) return null

  const normalized: string = idp.toLowerCase()
  if (normalized.includes('google')) return 'google'
  if (normalized.includes('facebook')) return 'facebook'
  return 'email'
}

/**
 * Résout la méthode d'inscription après retour OAuth MSAL.
 * @param {MsalIdTokenClaims} claims - Claims parsés du jeton d'identité.
 * @param {SocialSignupMethod | null} pendingMethod - Méthode posée avant redirect (`/register`).
 * @param {string} userId - Identifiant utilisateur stable.
 * @returns {Promise<SignupMethod | null>} Méthode à envoyer dans `signup_completed`, ou `null`.
 */
async function resolveSignupMethodOnOAuthCallback(
  claims: MsalIdTokenClaims,
  pendingMethod: SocialSignupMethod | null,
  userId: string,
): Promise<SignupMethod | null> {
  if (pendingMethod) return pendingMethod
  if (hasTrackedSignupForUser(userId)) return null

  const fromIdp: SignupMethod | null = idpToSignupMethod(claims.idp)
  if (!fromIdp || fromIdp === 'email') return null

  if (isMsalNewUserClaim(claims)) return fromIdp

  // const agentInfos = useAgentInformationsStore()
  // if (await agentInfos.isOnBoarded()) return null

  return fromIdp
}

/**
 * Émet `signup_completed` après callback OAuth si une inscription est détectée.
 * @param {string} userId - Identifiant utilisateur stable.
 * @param {MsalIdTokenClaims} claims - Claims parsés du jeton d'identité.
 * @param {SocialSignupMethod | null} pendingMethod - Méthode posée avant redirect (`/register`).
 * @returns {Promise<void>}
 */
export async function trackSignupCompletedOnOAuthCallback(
  userId: string,
  claims: MsalIdTokenClaims,
  pendingMethod: SocialSignupMethod | null,
): Promise<void> {
  if (!import.meta.client) return

  const method: SignupMethod | null = await resolveSignupMethodOnOAuthCallback(
    claims,
    pendingMethod,
    userId,
  )
  if (!method) return

  const { track } = useTracking()
  track(TRACKING_EVENTS.SIGNUP_COMPLETED, { method })
  markSignupTrackedForUser(userId)
}

/**
 * Enrichit PostHog depuis `GET /api/Profile/me` (identité : email, nom).
 * @param {string} userId - Identifiant stable utilisateur.
 * @returns {Promise<void>} Promesse résolue après identify / setPersonProperties.
 */
export async function enrichPostHogProfil(userId: string): Promise<void> {
  if (!import.meta.client) return

  const { identify } = useTracking()
  const fallbackEmail: string | null = await getMsalActiveAccountEmail()

  const userStore = useUserStore()

  try {
    const { data } = await useFrontApi().get<UserProfileDto>('/api/Profile/me')

    userStore.setProfilFromMe(data, fallbackEmail)

    const firstName: string | undefined = data.firstName?.trim() || undefined
    const lastName: string | undefined = data.lastName?.trim() || undefined
    const fullName: string | undefined =
      [firstName, lastName].filter(Boolean).join(' ') || undefined

    // UserProfileDto ne porte pas villes / intérêts (à enrichir côté FrontApi).
    identify(userId, {
      email: data.email?.trim() || fallbackEmail || undefined,
      name: fullName,
      firstName,
      lastName,
      phone: data.phone?.trim() || undefined,
    })
  } catch {
    if (fallbackEmail) {
      userStore.updateProfile({ email: fallbackEmail })
      identify(userId, { email: fallbackEmail })
    }
  }
}

/**
 * Émet `cookie_consent` au clic sur le bandeau analytics.
 * PostHog démarre en opt-out : pour un refus, opt-in temporaire puis opt-out.
 * @param {CookieConsentChoice} choice - `accepted` ou `rejected`.
 * @returns {void}
 */
export function trackCookieConsent(choice: CookieConsentChoice): void {
  if (!import.meta.client) return

  const posthog = usePostHog()
  if (!posthog) return

  if (choice === 'rejected') {
    posthog.opt_in_capturing()
  }

  posthog.capture(TRACKING_EVENTS.COOKIE_CONSENT, { choice })

  if (choice === 'rejected') {
    posthog.opt_out_capturing()
  }
}

/** Events émis avant le chargement de posthog-js, rejoués dès qu'il est prêt (sinon perdus sur cold load). */
const eventsWaitingForPostHog: Array<() => void> = []

let isWaitingForPostHog: boolean = false

const RETRY_DELAY_MS: number = 500

const MAX_RETRIES: number = 20

/**
 * Envoie les events en attente, dans l'ordre d'émission. No-op si posthog-js n'est pas chargé.
 * @returns {void}
 */
function sendQueuedEvents(): void {
  if (!usePostHog()) return
  while (eventsWaitingForPostHog.length > 0) {
    eventsWaitingForPostHog.shift()?.()
  }
}

/**
 * Attend le chargement de posthog-js (cold load) puis envoie les events en attente.
 * @param {number} attemptsLeft - Tentatives restantes avant abandon.
 * @returns {void}
 */
function waitForPostHogThenSend(attemptsLeft: number): void {
  if (usePostHog()) {
    isWaitingForPostHog = false
    sendQueuedEvents()
    return
  }
  if (attemptsLeft <= 0) {
    isWaitingForPostHog = false
    return
  }
  setTimeout(() => waitForPostHogThenSend(attemptsLeft - 1), RETRY_DELAY_MS)
}

/**
 * Wrapper fin et typé autour de PostHog.
 *
 * Toute la complexité (SSR-guard, null-safety, consentement) est centralisée ici :
 * `usePostHog()` renvoie `undefined` côté serveur ou tant que le script n'est pas
 * chargé, et la capture est inactive tant que le consentement n'est pas accordé
 * (cf. {@link useAnalyticsConsent}). Les events émis avant le chargement du SDK sont
 * mis en file puis rejoués une fois posthog-js prêt.
 * @returns {UseTrackingReturn} Fonctions `track`, `identify`, `setPersonProperties` et `reset`.
 */
export function useTracking(): UseTrackingReturn {
  /**
   * Émet un event custom typé vers PostHog.
   * @param {E} event - Nom de l'event issu du catalogue `TRACKING_EVENTS`.
   * @param {TrackingEventPayloads[E]} [properties] - Propriétés métier de l'event.
   * @returns {void}
   */
  function track<E extends TrackingEventName>(
    event: E,
    properties?: TrackingEventPayloads[E],
  ): void {
    const posthog = usePostHog()
    if (posthog) {
      sendQueuedEvents()
      posthog.capture(event, properties)
      return
    }

    // SSR : on ne met pas en file (état module partagé entre requêtes).
    if (!import.meta.client) return
    eventsWaitingForPostHog.push(() => usePostHog()?.capture(event, properties))
    if (!isWaitingForPostHog) {
      isWaitingForPostHog = true
      waitForPostHogThenSend(MAX_RETRIES)
    }
  }

  /**
   * Rattache les events à une identité utilisateur connue.
   * @param {string} distinctId - Identifiant stable de l'utilisateur (userId).
   * @param {TrackingUserProperties} [properties] - Propriétés de la personne.
   * @returns {void}
   */
  function identify(distinctId: string, properties?: TrackingUserProperties): void {
    usePostHog()?.identify(distinctId, toPostHogIdentifyProperties(properties))
  }

  /**
   * Enrichit les propriétés de la personne courante (sans émettre d'event métier).
   * @param {TrackingPersonProperties} properties - Attributs de profil à poser.
   * @returns {void}
   */
  function setPersonProperties(properties: TrackingPersonProperties): void {
    usePostHog()?.setPersonProperties(properties)
  }

  /**
   * Réinitialise l'identité PostHog (déconnexion) pour repartir d'un visiteur anonyme.
   * @returns {void}
   */
  function reset(): void {
    usePostHog()?.reset()
  }

  return {
    track,
    identify,
    setPersonProperties,
    reset,
  }
}
